// Dialog modal de confirmacion para acciones destructivas.
//
// Uso: reutilizado por M9.4 (redrive), M9.5 (delete plan), M9.6 (block,
// logout-all, rotate secrets). Por eso vive en components/, no en pages/.
//
// A11y:
//   - <dialog> nativo con focus-trap del browser.
//   - aria-labelledby + aria-describedby explicitos.
//   - Esc cierra (via el `onCancel` del form method="dialog").

import { useEffect, useRef, type ReactNode } from 'react';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  danger = false,
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element | null {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    if (!open && dlg.open) dlg.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby="confirm-title"
      aria-describedby="confirm-desc"
      onClose={onCancel}
      className="rounded-xl p-0 max-w-md w-full bg-surface dark:bg-surface-dark text-ink dark:text-ink-inverted backdrop:bg-black/50"
    >
      <div className="p-6 space-y-4">
        <h2 id="confirm-title" className="text-lg font-semibold">
          {title}
        </h2>
        <div id="confirm-desc" className="text-sm text-ink-muted">
          {description}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="min-h-[2.75rem] rounded-lg border border-surface-border dark:border-surface-dark-border px-4 py-2 text-sm hover:bg-surface-muted dark:hover:bg-surface-dark-muted disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`min-h-[2.75rem] rounded-lg px-4 py-2 text-sm font-medium text-white focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 ${
              danger
                ? 'bg-status-error hover:opacity-90 focus-visible:ring-status-error'
                : 'bg-brand hover:bg-brand-hover focus-visible:ring-brand-light'
            }`}
          >
            {loading ? 'Procesando…' : confirmLabel}
          </button>
        </div>
      </div>
    </dialog>
  );
}
